/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { formatPhoneNumber } from '../utils/phoneFormat';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactComponent as UserSvgIcon } from '../assets/icons/user.svg';
import { ReactComponent as ImageSvgIcon } from '../assets/icons/image.svg';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SecurityIcon from '@mui/icons-material/Security';
import { ReactComponent as StoreSvgIcon } from '../assets/icons/store.svg';
import { ReactComponent as SpeechBubbleSvgIcon } from '../assets/icons/speech_bubble.svg';
import LogoutIcon from '@mui/icons-material/Logout';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarStoreIcon } from '../assets/icons/sidebar_store_settings.svg';
import { ReactComponent as SidebarUserIcon } from '../assets/icons/sidebar_user_settings.svg';
import { ReactComponent as SidebarLineNotifIcon } from '../assets/icons/sidebar_line_notification.svg';
import { ReactComponent as StoreAvatarPlaceholderIcon } from '../assets/icons/store-avatar-placeholder.svg';
import AddItemButton from './AddItemButton';

import api, { storeApi, extractFieldErrorFromApiError, getApiErrorMessage } from '../services/api';
import { NotificationSetting, StoreNotificationIdCreate } from '../types/index';
import { useAuth } from '../auth/AuthContext';
import { IMAGE_ACCEPT_JPEG_PNG, IMAGE_TYPE_ERROR_MESSAGE, isJpegOrPng } from '../utils/imageUpload';

// ── スタイル定数 ────────────────────────────────────────

const normalFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    '& fieldset': { border: '1px solid #E0E0E0' },
    '&:hover fieldset': { border: '1px solid #BDBDBD' },
    '&.Mui-focused fieldset': { border: '1px solid #90CAF9' },
    '& input': { color: '#212121', fontSize: '15px' },
    '& input::placeholder': { color: '#BDBDBD', opacity: 1 },
  },
};

const secureFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    '& fieldset': { border: '1px solid #E0E0E0' },
    '&:hover fieldset': { border: '1px solid #BDBDBD' },
    '&.Mui-focused fieldset': { border: '1px solid #90CAF9' },
    '& input': { color: '#212121', fontSize: '15px' },
    '& input::placeholder': { color: '#BDBDBD', opacity: 1 },
  },
};

// ── 型定義 ──────────────────────────────────────────────

interface InitialSetupModalProps {
  open: boolean;
  onComplete: () => void;
  storeId: string;
}

// ── 定数 ────────────────────────────────────────────────

const STEPS = ['店舗設定', 'ユーザー設定', 'LINE通知設定'];
const MAX_USERS = 3;

// ── バリデーション ───────────────────────────────────────

const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const validateLiffId = (v: string) => /^[0-9]{10}-[a-zA-Z0-9]{8}$/.test(v);
const validateChannelSecret = (v: string) => /^[a-zA-Z0-9/]+$/.test(v);
const validateChannelToken = (v: string) => /^[a-zA-Z0-9+/=]+$/.test(v);

interface UserInput {
  name: string;
  email: string;
}

// ── コンポーネント ───────────────────────────────────────

const InitialSetupModal: React.FC<InitialSetupModalProps> = ({ open, onComplete, storeId }) => {
  const { signOut } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }, 100);
  };

  // ── STEP1: 店舗設定 ──────────────────────────────────
  const [storeData, setStoreData] = useState<any>(null);
  const [storeForm, setStoreForm] = useState({
    name: '',
    phone: '',
    liff_id: '',
    line_channel_secret: '',
    line_channel_access_token: '',
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── STEP2: ユーザー設定 ─────────────────────────────────
  const [registeredUsers, setRegisteredUsers] = useState<UserInput[]>([]);
  const [currentName, setCurrentName] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');

  // ── STEP3: LINE通知設定 ──────────────────────────────
  const [notificationSettings, setNotificationSettings] = useState<NotificationSetting[]>([]);

  const createNewNotification = (order: number): NotificationSetting => ({
    tempId: `new_${Date.now()}_${Math.random()}`,
    notification_id: '',
    label: `通知${order}`,
    display_order: order,
    isNew: true,
    isModified: false,
  });

  // モーダルが開いたら店舗情報を取得
  useEffect(() => {
    if (!open || !storeId) return;

    const fetchStoreData = async () => {
      try {
        const [detail, info] = await Promise.all([
          api.adminStore.getStoreDetail(storeId),
          storeApi.getStoreInfo(storeId),
        ]);
        const combined = { ...info, ...detail };
        setStoreData(combined);
        setStoreForm({
          name: combined.name || '',
          phone: combined.phone || '',
          liff_id: detail.liff_id || '',
          line_channel_secret: '',
          line_channel_access_token: '',
        });
        if (info.image_url) setImagePreview(info.image_url);
      } catch {
        // 取得失敗の場合は空フォームのまま
      }
    };

    fetchStoreData();
    setActiveStep(0);
    setRegisteredUsers([]);
    setCurrentName('');
    setCurrentEmail('');
    setNotificationSettings([createNewNotification(1)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storeId]);

  // ── STEP1 ハンドラ ────────────────────────────────────

  const handleStoreFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processed = value;
    if (name === 'phone') {
      processed = formatPhoneNumber(value);
    } else if (['liff_id', 'line_channel_secret', 'line_channel_access_token'].includes(name)) {
      processed = value.replace(/\s/g, '');
    }
    setStoreForm(prev => ({ ...prev, [name]: processed }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('画像ファイルのサイズは5MB以下にしてください'); return; }
    if (!isJpegOrPng(file)) { setError(IMAGE_TYPE_ERROR_MESSAGE); return; }
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    setError(null);
  };

  // ── STEP2 ハンドラ ────────────────────────────────────

  const addRegisteredUser = () => {
    setError(null);
    const name = currentName.trim();
    const email = currentEmail.trim();
    const errors: Record<string, string> = {};
    if (!name) errors.currentName = '管理者名を入力してください';
    if (!email) errors.currentEmail = 'メールアドレスを入力してください';
    else if (!validateEmail(email)) errors.currentEmail = '無効なメールアドレスです';
    else if (registeredUsers.some(u => u.email === email)) errors.currentEmail = 'このメールアドレスは既に追加されています';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }
    setFieldErrors({});
    setRegisteredUsers(prev => [...prev, { name, email }]);
    setCurrentName('');
    setCurrentEmail('');
  };

  const removeRegisteredUser = (index: number) => {
    setRegisteredUsers(prev => prev.filter((_, i) => i !== index));
  };

  // ── STEP3 ハンドラ ────────────────────────────────────

  const addNotificationSetting = () => {
    const order = notificationSettings.length + 1;
    setNotificationSettings(prev => [...prev, createNewNotification(order)]);
  };

  const removeNotificationSetting = (tempId: string) => {
    if (notificationSettings.length <= 1) return;
    setNotificationSettings(prev =>
      prev.filter(s => s.tempId !== tempId).map((s, i) => ({
        ...s,
        display_order: i + 1,
        label: `通知${i + 1}`,
      }))
    );
  };

  const updateNotificationId = (tempId: string, value: string) => {
    setNotificationSettings(prev =>
      prev.map(s => s.tempId === tempId ? { ...s, notification_id: value, isModified: true } : s)
    );
  };

  // ── 全ステップ一括送信（STARTボタン押下時のみ実行） ──

  const handleSubmitAll = async (): Promise<boolean> => {
    setIsSaving(true);

    try {
      // STEP1: 店舗情報を保存
      console.log('STEP1: 店舗情報を保存中...');
      const updateData: any = {
        name: storeForm.name,
      };
      if (storeForm.phone.trim()) updateData.phone = storeForm.phone;
      if (storeForm.liff_id.trim()) updateData.liff_id = storeForm.liff_id;
      if (storeForm.line_channel_secret.trim()) updateData.line_channel_secret = storeForm.line_channel_secret;
      if (storeForm.line_channel_access_token.trim()) updateData.line_channel_access_token = storeForm.line_channel_access_token;

      const updated = await api.adminStore.updateStore(storeId, updateData);
      setStoreData((prev: any) => ({ ...prev, ...updated }));

      if (selectedImage) {
        await storeApi.uploadStoreImage(storeId, selectedImage);
      }
      console.log('STEP1: 完了');

      // STEP2: ユーザーを作成（パスワードはバックエンドで自動生成しメール送信）
      for (const { email, name } of registeredUsers) {
        await api.adminIdentityUser.create({
          email,
          displayName: name,
          storeId,
        });
      }

      // STEP3: 通知設定を保存
      const validNotifications = notificationSettings.filter(s => s.notification_id.trim());
      if (validNotifications.length > 0) {
        console.log('STEP3: 通知設定保存中...', validNotifications);
        await Promise.all(
          validNotifications.map(s =>
            api.storeNotification.create({
              store_id: storeId,
              notification_id: s.notification_id.trim(),
              label: s.label,
              display_order: s.display_order,
            } as StoreNotificationIdCreate)
          )
        );
        console.log('STEP3: 完了');
      } else {
        console.log('STEP3: スキップ（通知ID未入力）');
      }

      return true;
    } catch (err: any) {
      console.error('InitialSetup保存エラー:', err);
      console.error('エラー詳細:', err?.response?.data || err?.message);

      // 競合などでバックエンドが特定フィールドに紐付くエラーを返した場合のフォールバック
      // （事前にSTEP1のhandleNextで重複チェック済みだが、同時更新等で漏れる可能性に備える）
      const fieldError = extractFieldErrorFromApiError(err);
      if (fieldError) {
        setFieldErrors(prev => ({ ...prev, [fieldError.field]: fieldError.message }));
        setActiveStep(0);
        scrollToField(fieldError.field);
        return false;
      }

      if (
        err.message?.includes('already exists') ||
        err.message?.includes('EMAIL_EXISTS') ||
        err.message?.includes('EMAIL_ALREADY_EXISTS') ||
        err.message?.includes('既に使用されています') ||
        err?.response?.data?.detail?.includes?.('already exists') ||
        err?.response?.data?.detail?.includes?.('EMAIL_EXISTS')
      ) {
        setError('登録済みのメールアドレスが含まれています');
      } else {
        setError(getApiErrorMessage(err, '登録処理に失敗しました。入力内容を確認してください'));
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // ── ナビゲーション ────────────────────────────────────

  const handleNext = async () => {
    setError(null);
    setFieldErrors({});
    // STEP1: フロント側バリデーション + LINE認証情報の重複チェック（API）
    if (activeStep === 0) {
      const errors: Record<string, string> = {};
      if (!storeForm.name.trim()) errors.name = '店舗名は必須です';
      if (storeForm.liff_id && !validateLiffId(storeForm.liff_id)) {
        errors.liff_id = 'LIFF IDは数値10桁+ハイフン+英数8桁の形式で入力してください（例：1234567890-abcdefgh）';
      }
      if (storeForm.line_channel_secret && !validateChannelSecret(storeForm.line_channel_secret)) {
        errors.line_channel_secret = 'LINEチャネルシークレットは半角英数字とスラッシュのみ使用可能です';
      }
      if (storeForm.line_channel_access_token && !validateChannelToken(storeForm.line_channel_access_token)) {
        errors.line_channel_access_token = 'LINEチャネルアクセストークンは半角英数字と記号(+/=)のみ使用可能です';
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        scrollToField(Object.keys(errors)[0]);
        return;
      }
      setFieldErrors({});

      // LINE認証情報の重複チェック（いずれか入力されている場合のみAPI実行）
      if (storeForm.line_channel_secret.trim() || storeForm.line_channel_access_token.trim()) {
        try {
          const result = await api.adminStore.checkLineCredentials({
            line_channel_access_token: storeForm.line_channel_access_token.trim() || undefined,
            line_channel_secret: storeForm.line_channel_secret.trim() || undefined,
            exclude_store_id: storeId,
          });
          if (!result.is_available && result.conflicting_field) {
            setFieldErrors({ [result.conflicting_field]: result.message || '入力された値は既に別の店舗に登録されています' });
            scrollToField(result.conflicting_field);
            return;
          }
        } catch (err: any) {
          console.error('LINE認証情報の重複チェック失敗:', err);
          setError('LINE認証情報の重複チェックに失敗しました。通信状況を確認してもう一度お試しください。');
          return;
        }
      }
    }
    // STEP2: バリデーション
    if (activeStep === 1) {
      if (registeredUsers.length === 0) {
        const errors: Record<string, string> = {};
        if (!currentName.trim()) errors.currentName = '管理者名を入力してください';
        if (!currentEmail.trim()) {
          errors.currentEmail = 'メールアドレスを入力し「IDを追加」ボタンで登録してください';
        } else if (!validateEmail(currentEmail.trim())) {
          errors.currentEmail = '無効なメールアドレスです';
        } else {
          errors.currentEmail = '入力後「IDを追加」ボタンで登録してください';
        }
        setFieldErrors(errors);
        scrollToField(Object.keys(errors)[0]);
        return;
      }
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setFieldErrors({});
    setActiveStep(prev => prev - 1);
  };

  const handleComplete = async () => {
    setError(null);
    // STEP3 フロント側バリデーション
    const errors: Record<string, string> = {};
    const valid = notificationSettings.filter(s => s.notification_id.trim());
    if (valid.length === 0) {
      // 最初の通知IDフィールドにエラーを表示
      errors[`notification_${notificationSettings[0]?.tempId}`] = '少なくとも1つの通知IDを入力してください';
    } else {
      // 重複チェック
      const seen = new Map<string, string>();
      notificationSettings.forEach(s => {
        const id = s.notification_id.trim();
        if (!id) return;
        if (seen.has(id)) {
          errors[`notification_${s.tempId}`] = '通知IDが重複しています';
        } else {
          seen.set(id, s.tempId);
        }
      });
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }
    setFieldErrors({});

    // 全ステップのデータをまとめてAPIに送信
    const ok = await handleSubmitAll();
    if (!ok) return;
    onComplete();
  };

  // ── ページインジケーター ──────────────────────────────

  const PageIndicator = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '30%' }}>
        {STEPS.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: activeStep === i ? 10 : 8,
              height: activeStep === i ? 10 : 8,
              borderRadius: '50%',
              bgcolor: activeStep === i ? '#1976D2' : '#BDBDBD',
              transition: 'all 0.2s',
            }}
          />
        ))}
      </Box>
    </Box>
  );

  // ── ステップ別ヘッダー設定 ────────────────────────────

  const stepHeaders = [
    {
      icon: <StoreSvgIcon style={{ width: 22, height: 22 }} />,
      title: '店舗設定',
      subtitle: 'まずは、あなたの店舗情報を設定',
    },
    {
      icon: <UserSvgIcon style={{ width: 22, height: 22 }} />,
      title: 'ユーザー設定',
      subtitle: 'ユーザー管理用のメールアドレスを設定',
    },
    {
      icon: <SpeechBubbleSvgIcon style={{ width: 22, height: 22 }} />,
      title: 'LINE通知設定',
      subtitle: 'LINEで通知を受け取る為の個人IDを設定',
    },
  ];

  // ── STEP1 コンテンツ ──────────────────────────────────

  const Step1Content = (
    <Box>
      {/* 店舗画像アップロード */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={imagePreview || undefined}
            sx={{ width: 90, height: 90, bgcolor: '#F5F5F5', border: '1px solid #E0E0E0' }}
          >
            <ImageSvgIcon style={{ width: 28, height: 28 }} />
          </Avatar>
          {imagePreview && (
            <IconButton
              size="small"
              onClick={() => { setImagePreview(null); setSelectedImage(null); }}
              sx={{
                position: 'absolute', top: -4, right: -4,
                bgcolor: 'white', width: 24, height: 24,
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: '#fafafa' },
              }}
            >
              <DeleteIcon sx={{ fontSize: 14, color: '#f44336' }} />
            </IconButton>
          )}
        </Box>
        <AddItemButton
          onClick={() => fileInputRef.current?.click()}
          label="画像を挿入する"
          showIcon={false}
          sx={{ mt: -1.5, zIndex: 1 }}
        />
        <input ref={fileInputRef} type="file" accept={IMAGE_ACCEPT_JPEG_PNG} onChange={handleImageSelect} style={{ display: 'none' }} />
      </Box>

      {/* 店舗ID */}
      <Typography
        fontWeight="bold"
        sx={{ textAlign: 'center', color: '#3c3c3c', mb: 3, fontSize: '14px' }}
      >
        店舗ID / {storeId || '-'}
      </Typography>

      {/* 店舗名 */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>店舗名</Typography>
        <TextField
          name="name"
          value={storeForm.name}
          onChange={handleStoreFormChange}
          fullWidth
          placeholder="店舗名を入力"
          error={!!fieldErrors.name}
          helperText={fieldErrors.name}
          sx={normalFieldSx}
        />
      </Box>

      {/* 住所（読み取り専用） */}
      {storeData?.address && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>住所</Typography>
          <Box
            sx={{
              bgcolor: '#F5F5F5',
              borderRadius: '12px',
              px: 2,
              py: 1.75,
              minHeight: 50,
            }}
          >
            <Typography sx={{ fontSize: '14px', color: '#212121' }}>{storeData.address}</Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#BDBDBD', mt: 0.5 }}>
            ※ 住所を変更したい場合はお問い合わせください
          </Typography>
        </Box>
      )}

      {/* 電話番号 */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>電話番号</Typography>
        <TextField
          name="phone"
          value={storeForm.phone}
          onChange={handleStoreFormChange}
          fullWidth
          type="tel"
          placeholder="例: 03-1234-5678"
          sx={normalFieldSx}
        />
      </Box>

      {/* LIFF ID */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>LIFF ID</Typography>
        <TextField
          name="liff_id"
          value={storeForm.liff_id}
          onChange={handleStoreFormChange}
          fullWidth
          placeholder="LIFF ID"
          error={!!fieldErrors.liff_id}
          helperText={fieldErrors.liff_id || '数値10桁+ハイフン+英数8桁'}
          FormHelperTextProps={{
            sx: fieldErrors.liff_id
              ? { color: '#d32f2f', fontSize: '12px', mt: 0.5 }
              : { color: '#BDBDBD', fontSize: '11px', mt: 0.5 },
          }}
          sx={secureFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <VpnKeyIcon sx={{ color: '#9E9E9E', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* LINEチャネルシークレット */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>LINEチャネルシークレット</Typography>
        <TextField
          name="line_channel_secret"
          value={storeForm.line_channel_secret}
          onChange={handleStoreFormChange}
          fullWidth
          placeholder="LINEチャネルシークレット"
          type="password"
          autoComplete="new-password"
          error={!!fieldErrors.line_channel_secret}
          helperText={fieldErrors.line_channel_secret || '半角英数字とスラッシュのみ使用可能'}
          FormHelperTextProps={{
            sx: fieldErrors.line_channel_secret
              ? { color: '#d32f2f', fontSize: '12px', mt: 0.5 }
              : { color: '#BDBDBD', fontSize: '11px', mt: 0.5 },
          }}
          sx={secureFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SecurityIcon sx={{ color: '#9E9E9E', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* LINEチャネルアクセストークン */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>LINEチャネルアクセストークン</Typography>
        <TextField
          name="line_channel_access_token"
          value={storeForm.line_channel_access_token}
          onChange={handleStoreFormChange}
          fullWidth
          placeholder="LINEチャネルアクセストークン"
          type="password"
          autoComplete="new-password"
          error={!!fieldErrors.line_channel_access_token}
          helperText={fieldErrors.line_channel_access_token || '半角英数字と記号(+/=)が使用可能'}
          FormHelperTextProps={{
            sx: fieldErrors.line_channel_access_token
              ? { color: '#d32f2f', fontSize: '12px', mt: 0.5 }
              : { color: '#BDBDBD', fontSize: '11px', mt: 0.5 },
          }}
          sx={secureFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SecurityIcon sx={{ color: '#9E9E9E', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );

  // ── STEP2 コンテンツ ──────────────────────────────────

  const Step2Content = (
    <Box>
      <Typography sx={{ fontSize: '13px', color: '#898989', mb: 2.5, textAlign: 'center' }}>
        最低1名、最大3名のユーザー登録が必要です
        <br />
        登録完了後、各ユーザーにパスワード設定メールが届きます。
      </Typography>

      {/* 登録済みユーザー一覧 */}
      {registeredUsers.length > 0 && (
        <Stack spacing={1.5} sx={{ mb: 2.5 }}>
          {registeredUsers.map((user, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#F5F5F5',
                borderRadius: '12px',
                px: 2,
                py: 1.5,
                border: '1px solid #E0E0E0',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '14px', color: '#212121', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#616161', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </Typography>
              </Box>
              <IconButton
                color="error"
                size="small"
                onClick={() => removeRegisteredUser(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {/* 名前・メールアドレス入力 + IDを追加ボタン（上限未満の場合のみ表示） */}
      {registeredUsers.length < MAX_USERS && (
        <>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>
              管理者名
            </Typography>
            <TextField
              name="currentName"
              value={currentName}
              onChange={e => {
                setCurrentName(e.target.value);
                if (fieldErrors.currentName) {
                  setFieldErrors(prev => { const next = { ...prev }; delete next.currentName; return next; });
                }
              }}
              fullWidth
              placeholder="管理者名を入力"
              autoComplete="off"
              error={!!fieldErrors.currentName}
              helperText={fieldErrors.currentName}
              sx={normalFieldSx}
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>
              メールアドレス
            </Typography>
            <TextField
              name="currentEmail"
              type="email"
              value={currentEmail}
              onChange={e => {
                setCurrentEmail(e.target.value);
                if (fieldErrors.currentEmail) {
                  setFieldErrors(prev => { const next = { ...prev }; delete next.currentEmail; return next; });
                }
              }}
              fullWidth
              placeholder="example@email.com"
              autoComplete="off"
              error={!!fieldErrors.currentEmail}
              helperText={fieldErrors.currentEmail}
              sx={normalFieldSx}
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <AddItemButton onClick={addRegisteredUser} />
          </Box>
        </>
      )}
    </Box>
  );

  // ── STEP3 コンテンツ ──────────────────────────────────

  const Step3Content = (
    <Box>
      <Typography sx={{ fontSize: '13px', color: '#898989', mb: 3, lineHeight: 1.7, textAlign: 'center' }}>
        通知を希望される方のLINEから、店舗公式LINEに<br />
        「通知登録」とメッセージをお送りください。
      </Typography>

      {notificationSettings.map((setting, index) => {
        const errorKey = `notification_${setting.tempId}`;
        return (
          <Box key={setting.tempId} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>
              ID:{String(index + 1).padStart(3, '0')}
            </Typography>
            <TextField
              fullWidth
              name={errorKey}
              placeholder="個人ID"
              value={setting.notification_id}
              onChange={e => {
                updateNotificationId(setting.tempId, e.target.value);
                if (fieldErrors[errorKey]) {
                  setFieldErrors(prev => { const next = { ...prev }; delete next[errorKey]; return next; });
                }
              }}
              error={!!fieldErrors[errorKey]}
              helperText={fieldErrors[errorKey]}
              sx={normalFieldSx}
              InputProps={notificationSettings.length > 1 ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="error"
                      onClick={() => removeNotificationSetting(setting.tempId)}
                      edge="end"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              } : undefined}
            />
          </Box>
        );
      })}

      <AddItemButton onClick={addNotificationSetting} />
    </Box>
  );

  // ── メインレンダリング ─────────────────────────────────

  const stepContents = [Step1Content, Step2Content, Step3Content];
  const currentHeader = stepHeaders[activeStep];
  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      scroll="paper"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: '#FFFFFF',
          borderRadius: '24px 24px 0 0',
          m: 0,
          width: '100%',
          maxWidth: '100% !important',
          height: 'calc(100vh - 48px)',
          maxHeight: 'calc(100vh - 48px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ヘッダー（固定） */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#FFFFFF',
          textAlign: 'center',
          px: 3, pt: 3, pb: 2.5,
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          {currentHeader.icon}
          <Typography fontWeight="bold" sx={{ color: '#3c3c3c', fontSize: '17px' }}>
            {currentHeader.title}
          </Typography>
          <IconButton
            onClick={signOut}
            size="small"
            title="ログアウト"
            sx={{ position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)', color: '#9E9E9E' }}
          >
            <LogoutIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Typography sx={{ color: '#3c3c3c', mt: 0.5, fontSize: '18px' }}>
          {currentHeader.subtitle}
        </Typography>
      </Box>

      {/* コンテンツ（スクロール領域） */}
      <DialogContent sx={{ p: 0, flex: '1 1 auto', overflowY: 'auto' }}>
        <Box sx={{ bgcolor: '#FFFFFF', p: 2.5 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {stepContents[activeStep]}
        </Box>
      </DialogContent>

      {/* フッター（固定） */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#FFFFFF',
          px: 2, pb: 3, pt: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {/* 戻るボタン（STEP2以降に表示） */}
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              disabled={isSaving}
              sx={{
                flex: 1,
                bgcolor: '#c4c4c4',
                color: '#3c3c3c',
                borderRadius: '12px',
                height: 52,
                fontSize: '15px',
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#adadad', boxShadow: 'none' },
              }}
            >
              戻る
            </Button>
          )}
          {/* 次へ進む / STARTボタン */}
          <Button
            variant="contained"
            onClick={isLastStep ? handleComplete : handleNext}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              flex: activeStep > 0 ? 3 : 1,
              bgcolor: '#0071e3',
              borderRadius: '12px',
              height: 52,
              fontSize: '16px',
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': { bgcolor: '#0062c4' },
              '&.Mui-disabled': { bgcolor: '#90A4AE', color: 'white' },
            }}
          >
            {isSaving ? '登録処理中...' : (isLastStep ? 'START' : '次へ進む')}
          </Button>
        </Box>
        <PageIndicator />
      </Box>
      </Box>
    </Dialog>
  );
};

export default InitialSetupModal;
